import type { Dayjs, ManipulateType } from 'dayjs';

import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useMemo } from 'react';

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

export const PARIS_TIMEZONE = 'Europe/Paris';

export type DateType = Date | Dayjs | number | string;

type DurationConfig = {
  date: DateType;
  duration: number;
  unit: ManipulateType;
};

export type TemplateDate =
  | 'à HH[h]mm'
  | 'D MMM.'
  | 'D MMMM YYYY'
  | 'D'
  | 'DD MMMM YYYY à HH[h]mm'
  | 'DD MMMM YYYY'
  | 'DD MMMM'
  | 'DD'
  | 'DD/MM'
  | 'DD/MM/YY'
  | 'DD/MM/YYYY'
  | 'dddd D MMMM à h:mm A'
  | 'dddd DD MMMM YY'
  | 'dddd DD MMMM'
  | 'MM'
  | 'MM/YYYY'
  | 'MMM YYYY'
  | 'MMMM YYYY'
  | 'MMMM'
  | 'YYYY-MM-DD'
  | 'YYYY';

/**
 * Creates a Day.js date object with French locale.
 *
 * @param date - The date to parse (Date, Dayjs, number timestamp, or string)
 * @param format - Optional format template for parsing string dates
 * @param strict - Whether to use strict parsing mode
 * @returns A Day.js date object with French locale
 *
 * @example
 * dayjsDate()                              // Current date/time
 * dayjsDate(new Date())                    // From Date object
 * dayjsDate('25/12/2025', 'DD/MM/YYYY')   // Parse with format
 * dayjsDate(1735128000000)                // From timestamp
 */
export const dayjsDate = (
  date?: DateType,
  format?: TemplateDate,
  strict?: boolean
): Dayjs => dayjs(date, format, strict).locale('fr-FR');

/**
 * Formats a date using a predefined template.
 *
 * @param date - The date to format (Date, Dayjs, number timestamp, or string)
 * @param template - The format template to use
 * @returns The formatted date string
 *
 * @example
 * // Available templates with examples (for 2025-12-25 14:30):
 * formatDate(date, 'à HH[h]mm')                  // "à 14h30"
 * formatDate(date, 'D MMM.')                     // "25 déc."
 * formatDate(date, 'D MMMM YYYY')                // "25 décembre 2025"
 * formatDate(date, 'D')                          // "25"
 * formatDate(date, 'DD MMMM YYYY à HH[h]mm')     // "25 décembre 2025 à 14h30"
 * formatDate(date, 'DD MMMM YYYY')               // "25 décembre 2025"
 * formatDate(date, 'DD MMMM')                    // "25 décembre"
 * formatDate(date, 'DD')                         // "25"
 * formatDate(date, 'DD/MM')                      // "25/12"
 * formatDate(date, 'DD/MM/YY')                   // "25/12/25"
 * formatDate(date, 'DD/MM/YYYY')                 // "25/12/2025"
 * formatDate(date, 'dddd D MMMM à h:mm A')       // "jeudi 25 décembre à 2:30 PM"
 * formatDate(date, 'dddd DD MMMM YY')            // "jeudi 25 décembre 25"
 * formatDate(date, 'dddd DD MMMM')               // "jeudi 25 décembre"
 * formatDate(date, 'MM')                         // "12"
 * formatDate(date, 'MM/YYYY')                    // "12/2025"
 * formatDate(date, 'MMM YYYY')                   // "déc. 2025"
 * formatDate(date, 'MMMM YYYY')                  // "décembre 2025"
 * formatDate(date, 'MMMM')                       // "décembre"
 * formatDate(date, 'YYYY-MM-DD')                 // "2025-12-25" (ISO format)
 * formatDate(date, 'YYYY')                       // "2025"
 */
export const formatDate = (date: DateType, template: TemplateDate): string => {
  return dayjsDate(date).format(template);
};

/**
 * Formats a date in the Europe/Paris timezone (handles CET/CEST DST automatically).
 * Use this when rendering an absolute date/time to end users (e.g. emails), so the
 * displayed time matches local Paris time regardless of the runtime timezone (Lambda
 * runs in UTC).
 *
 * @example
 * // '2026-12-31T23:59:59Z' → "1 janvier 2027 à 00h59" (CET, UTC+1)
 * formatDateInParis('2026-12-31T23:59:59Z', 'DD MMMM YYYY à HH[h]mm')
 */
export const formatDateInParis = (
  date: DateType,
  template: TemplateDate
): string => dayjsDate(date).tz(PARIS_TIMEZONE).format(template);

/**
 * Safely parses a date string to a Date object.
 * Returns undefined if the input is null, undefined, or an invalid date.
 *
 * @param dateStr - The date string to parse
 * @returns A valid Date object or undefined
 *
 * @example
 * parseDate('2025-12-09')           // Date object
 * parseDate('invalid')              // undefined
 * parseDate(undefined)              // undefined
 * parseDate(null)                   // undefined
 */
export const parseDate = (
  dateStr: string | null | undefined
): Date | undefined => {
  if (!dateStr) return undefined;
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? undefined : date;
};

/**
 * Adds a duration to a date.
 * @example addDuration({ date: new Date(), duration: 5, unit: 'day' })  // 5 days from now
 */
export const addDuration = ({
  date,
  duration,
  unit,
}: DurationConfig): Dayjs => {
  return dayjsDate(date).add(duration, unit);
};

const STRICT = true;

/**
 * Validates a birthdate string in DD/MM/YYYY format.
 * @example isBirthDateValid('25/12/1990')  // true
 */
export const isBirthDateValid = (date: string) =>
  dayjsDate(date, 'DD/MM/YYYY', STRICT).isValid();

/**
 * Converts DD/MM/YYYY to YYYY-MM-DD (ISO format).
 * @example formatBirthDate('25/12/1990')  // "1990-12-25"
 */
export const formatBirthDate = (date: string) =>
  dayjsDate(date, 'DD/MM/YYYY', STRICT).format('YYYY-MM-DD');

/**
 * Converts YYYY-MM-DD to DD/MM/YYYY format.
 * @example unformatBirthDate('1990-12-25')  // "25/12/1990"
 */
export const unformatBirthDate = (date: string) =>
  formatDate(date, 'DD/MM/YYYY');

export const millisecondsToSeconds = (milliseconds: number): number =>
  Math.floor(milliseconds / 1000);

export const getYearsUntil = (date: DateType): number => {
  const years = dayjsDate(date).diff(dayjsDate(), 'year', true);
  return Math.max(0, Math.round(years));
};

export const millisecondsToMinutes = (milliseconds: number): number =>
  millisecondsToSeconds(milliseconds) / 60;

/**
 * Calculates age in years from a birthdate.
 * @example getAge('1990-12-25')  // 35 (if current year is 2025)
 */
export const getAge = (date?: DateType): number => {
  const birthDate = dayjsDate(date);
  const now = dayjsDate();
  return now.diff(birthDate, 'year');
};

/**
 * Computes the "millésime" age: the difference between the current calendar year
 * and the birth year, ignoring the day/month. This is the age model mandated for
 * prévoyance & obsèques products (cf. spec: "tous les âges sont calculés en
 * millésime") — two people born the same year share the same millésime age
 * regardless of whether their birthday has occurred yet.
 * @example getMillesimeAge('2000-11-30') // 26 (in 2026, even on 2026-01-01)
 */
export const getMillesimeAge = (birthDate: DateType): number =>
  dayjsDate().year() - dayjsDate(birthDate).year();

const MINOR_AGE = 18;

/**
 * Checks if a person is under 18 years old.
 * @example isMinor('2010-01-01')  // true (if current year is 2025)
 */
export const isMinor = (birthDate: DateType) => {
  const age = getAge(dayjsDate(birthDate));
  return age < MINOR_AGE;
};

/**
 * Checks if age is less than specified threshold.
 * @example isYoungerThan('2000-01-01', 30)  // true (25 < 30)
 */
export const isYoungerThan = (date: DateType, age: number): boolean => {
  const birthDate = dayjsDate(date);
  const now = dayjsDate();
  return now.diff(birthDate, 'year') < age;
};

/**
 * Checks if age is greater than or equal to specified threshold.
 * @example isOlderThan('1990-01-01', 30)  // true (35 >= 30)
 */
export const isOlderThan = (date: DateType, age: number): boolean => {
  const birthDate = dayjsDate(date);
  const now = dayjsDate();
  return now.diff(birthDate, 'year') >= age;
};

/**
 * Subtracts a duration from a date.
 * @example subtractDuration({ date: new Date(), duration: 1, unit: 'year' })  // Last year
 */
export const subtractDuration = ({
  date,
  duration,
  unit,
}: DurationConfig): Dayjs => {
  return dayjsDate(date).subtract(duration, unit);
};

export const getYear = (date: DateType): number => dayjsDate(date).year();

/**
 * Today's date as an ISO `YYYY-MM-DD` string (local time).
 * @example todayIsoDate() // "2025-12-25"
 */
export const todayIsoDate = (): string => formatDate(new Date(), 'YYYY-MM-DD');

/**
 * React hook that returns current year and last year.
 * @example const { currentYear, lastYear } = useYears()  // { currentYear: "2025", lastYear: "2024" }
 */
export const useYears = () => {
  return useMemo(
    () => ({
      currentYear: formatDate(new Date(), 'YYYY'),
      lastYear: formatDate(
        subtractDuration({
          date: new Date(),
          duration: 1,
          unit: 'year',
        }),
        'YYYY'
      ),
    }),
    []
  );
};
